// pages/Analyse.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, Calendar, Clock, CheckCircle, XCircle, 
  TrendingUp, AlertTriangle, Brain, Lightbulb,
  Building2, BarChart3, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Analyse() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [delais, setDelais] = useState(null);
  const [topDep, setTopDep] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [selectedTab, setSelectedTab] = useState('apercu');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const ID_Societe = user?.ID_Societe || null;

  const moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  // ✅ Correction : useCallback pour fetchAllData
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisRes, absencesRes, evolutionRes, delaisRes, topRes, predRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/kpis`, { params: { ID_Societe, annee } }),
        axios.get(`${API_URL}/dashboard/absences-par-departement`, { params: { ID_Societe, annee } }),
        axios.get(`${API_URL}/dashboard/evolution-mensuelle`, { params: { ID_Societe, annee } }),
        axios.get(`${API_URL}/dashboard/delais-validation`, { params: { ID_Societe, annee } }),
        axios.get(`${API_URL}/dashboard/top-departements`, { params: { ID_Societe, annee } }),
        axios.get(`${API_URL}/dashboard/predictions`, { params: { ID_Societe } }),
      ]);

      if (kpisRes.data.success) setKpis(kpisRes.data.kpis);
      if (absencesRes.data.success) setAbsences(absencesRes.data.absences);
      if (evolutionRes.data.success) setEvolution(evolutionRes.data.evolution);
      if (delaisRes.data.success) setDelais(delaisRes.data.delais);
      if (topRes.data.success) setTopDep(topRes.data.top);
      if (predRes.data.success) setPredictions(predRes.data);
    } catch (err) {
      // Silencieux
    } finally {
      setLoading(false);
    }
  }, [annee, ID_Societe]); // ✅ Dépendances du useCallback

  // ✅ Correction : ajout de fetchAllData dans les dépendances
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]); // ✅ Dépendance corrigée

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  const maxAbsence = Math.max(...absences.map(a => a.taux_absence || 0), 1);
  const maxEvo = Math.max(...evolution.map(e => e.total || 0), 1);

  return (
    <div className="p-4 space-y-5 max-w-7xl mx-auto">
      
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart3 className="text-emerald-600" size={26} />
            Analyse & KPIs
          </h1>
          <p className="text-sm text-gray-500 mt-1">Tableau de bord RH • {annee}</p>
        </div>
        <select
          value={annee}
          onChange={(e) => setAnnee(parseInt(e.target.value))}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          {[2024, 2025, 2026].map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {/* KPIs Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={<Calendar size={22} />}
          value={kpis?.total_demandes || 0}
          label="Demandes totales"
          color="blue"
        />
        <KpiCard
          icon={<CheckCircle size={22} />}
          value={kpis?.approuvees || 0}
          label="Approuvées"
          color="emerald"
        />
        <KpiCard
          icon={<Clock size={22} />}
          value={kpis?.en_attente || 0}
          label="En attente"
          color="amber"
        />
        <KpiCard
          icon={<XCircle size={22} />}
          value={kpis?.rejetees || 0}
          label="Rejetées"
          color="red"
        />
      </div>

      {/* Onglets */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {[
          { id: 'apercu', label: 'Aperçu' },
          { id: 'absences', label: 'Absences' },
          { id: 'delais', label: 'Délais' },
          { id: 'ia', label: '🤖 IA' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
              selectedTab === tab.id 
                ? 'bg-white text-emerald-700 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des onglets */}
      {selectedTab === 'apercu' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Évolution mensuelle */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity size={16} className="text-emerald-500" />
              Évolution mensuelle
            </h3>
            <div className="flex items-end gap-2 h-40">
              {evolution.map((e, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-500">{e.total || 0}</span>
                  <div 
                    className="w-full bg-emerald-500 rounded-t-md transition-all hover:bg-emerald-400"
                    style={{ 
                      height: `${(e.total / maxEvo) * 100}%`,
                      minHeight: '4px'
                    }}
                  />
                  <span className="text-xs text-gray-400">{moisNoms[e.mois - 1]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top 5 départements */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Building2 size={16} className="text-purple-500" />
              Top 5 départements
            </h3>
            <div className="space-y-3">
              {topDep.map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">{d.departement}</span>
                      <span className="text-gray-400">{d.total_demandes} demandes</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${(d.total_demandes / (topDep[0]?.total_demandes || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Délais */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Clock size={16} className="text-blue-500" />
              Délais de validation
            </h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">{delais?.delai_moyen_jours || 0}</div>
                <div className="text-xs text-blue-400">Moyen (jours)</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-600">{delais?.delai_max_jours || 0}</div>
                <div className="text-xs text-amber-400">Maximum</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-emerald-600">{delais?.delai_min_jours || 0}</div>
                <div className="text-xs text-emerald-400">Minimum</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'absences' && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Users size={16} className="text-rose-500" />
            Taux d'absence par département
          </h3>
          <div className="space-y-4">
            {absences.map((a, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">{a.departement}</span>
                  <span className={`font-semibold ${a.taux_absence > 10 ? 'text-red-500' : 'text-gray-500'}`}>
                    {a.taux_absence}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all ${
                      a.taux_absence > 15 ? 'bg-red-500' : 
                      a.taux_absence > 8 ? 'bg-amber-500' : 
                      'bg-emerald-500'
                    }`}
                    style={{ width: `${(a.taux_absence / maxAbsence) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {a.total_employes} employés • {a.jours_absence} jours d'absence
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedTab === 'delais' && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Analyse des délais par niveau</h3>
          <p className="text-sm text-gray-400">Données détaillées des délais de validation</p>
        </div>
      )}

      {selectedTab === 'ia' && predictions && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Prédictions */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Brain size={16} className="text-purple-500" />
              Prédictions IA
            </h3>
            <div className="space-y-3">
              {predictions.predictions?.slice(0, 6).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{moisNoms[p.mois - 1]}</span>
                  <span className="font-medium text-gray-700">{p.demandes_prevues} prévues</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    p.niveau_risque === 'Élevé' ? 'bg-red-100 text-red-600' :
                    p.niveau_risque === 'Moyen' ? 'bg-amber-100 text-amber-600' :
                    'bg-emerald-100 text-emerald-600'
                  }`}>
                    {p.niveau_risque}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Alertes & Suggestions */}
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
              <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                <AlertTriangle size={16} />
                Alerte
              </h3>
              <p className="text-sm text-amber-600">
                {predictions.alerte || 'Aucune alerte détectée'}
              </p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
              <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                <Lightbulb size={16} />
                Suggestion
              </h3>
              <p className="text-sm text-emerald-600">
                {predictions.suggestion || 'Tous les indicateurs sont au vert'}
              </p>
            </div>

            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
              <h3 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
                <TrendingUp size={16} />
                Pic prévu
              </h3>
              {predictions.pic_prevu && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {predictions.pic_prevu.demandes_prevues}
                  </div>
                  <div className="text-sm text-purple-400">
                    demandes prévues en {moisNoms[predictions.pic_prevu.mois - 1]}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant KPI Card
function KpiCard({ icon, value, label, color }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-100 text-blue-600',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-600',
    amber: 'bg-amber-50 border-amber-100 text-amber-600',
    red: 'bg-red-50 border-red-100 text-red-600',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorMap[color]}`}>
      <div className="flex items-center gap-3">
        <div className="opacity-70">{icon}</div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs opacity-70">{label}</div>
        </div>
      </div>
    </div>
  );
}